import { EventEmitter, inject, Injectable } from '@angular/core';
import { Operation } from '@factor_ec/utils';
import { BeneficiaryFormComponent } from '../components/beneficiary-form/beneficiary-form.component';
import { MatDialog } from '@angular/material/dialog';
import { Beneficiary } from '../models/connection';
import { MessageService } from '@factor_ec/ui';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '@/core/api.service';
import { SessionService } from '@/core/session.service';
import { BeneficiaryRepository } from '@/main/repositories/beneficiary-repository';

@Injectable({
  providedIn: 'root'
})
export class ConnectionService {
  // Dependency injection
  private readonly apiService = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly messageService = inject(MessageService);
  private readonly sessionService = inject(SessionService);
  private readonly beneficiaryRepository = inject(BeneficiaryRepository);

  // Events
  public readonly change: EventEmitter<Operation> = new EventEmitter();
  mutations = this.beneficiaryRepository.mutations();

  /**
   * @description Abre el diálogo para crear o editar un beneficiario
   * @param {Beneficiary} [data] - Beneficiario existente para editar (opcional)
   * @returns {Promise<Operation>} Promesa con la operación resultante
   */
  public async openBeneficiary(data?: Beneficiary): Promise<Operation> {
    return new Promise<Operation>(resolve => {
      const dialogRef = this.dialog.open(BeneficiaryFormComponent, {
        data: {
          beneficiary: data
        },
        panelClass: 'ft-dialog',
        width: '400px'
      });
      dialogRef.componentInstance.afterSubmit.subscribe(
        (operation: Operation) => {
          this.change.emit(operation);
          resolve(operation);
        }
      );
    });
  }

  /**
   * @description Elimina un beneficiario tras confirmación del usuario
   * @param {Beneficiary} beneficiary - Beneficiario a eliminar
   * @returns {Promise<void>}
   */
  async removeBeneficiary(beneficiary: Beneficiary): Promise<void> {
    const value = await firstValueFrom(
      this.messageService.show(
        $localize`Do you really want to delete beneficiary "${beneficiary.description} (#${beneficiary.sequence})"?`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'error',
            class: 'text-danger ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Cancel`,
              value: 0,
              type: 'stroked',
              class: 'flex-grow-1'
            },
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      )
    );
    if (value === 1) {
      const data = {
        sequence: beneficiary.sequence,
        email: beneficiary.email
      }
      await this.mutations.delete(data)


      this.change.emit({
        method: 'delete',
        entity: beneficiary
      });
      this.messageService.show($localize`Beneficiary deleted successfully.`, {
        class: 'ft-message--success',
        icon: 'check--circle'
      });
    }
    return value;
  }
}
